import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useEffect } from "react";
import { SEO } from "@/components/SEO";
import { Link } from "react-router-dom";

export default function Privacy() {
  useEffect(() => {
    // Update meta tags for Privacy Policy page
    document.title = "Privacy Policy | ArqeraAI";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute("content", "Learn how ArqeraAI collects, uses, and protects your personal information. Our privacy policy outlines our commitment to data security and transparency.");
    }
  }, []);

  return (
    <div className="min-h-screen bg-deep-navy text-foreground">
      <SEO 
        title="Privacy Policy — ArqeraAI | Data Protection & Security"
        description="Learn how ArqeraAI collects, uses, and protects your personal information. Our privacy policy outlines our commitment to data security and transparency."
        canonical="https://arqeraai.com/privacy"
        noIndex={true}
      />
      {/* Header */}
      <header className="border-b border-cyan/20 bg-deep-navy/90 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <Button
            asChild
            variant="ghost"
            className="text-cyan hover:text-cyan/80 hover:bg-cyan/10"
          >
            <Link to="/">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Home
            </Link>
          </Button>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-16">
        <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
          Privacy Policy
        </h1>
        <p className="text-light-gray mb-12">Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>

        <div className="space-y-8 text-medium-gray">
          {/* Introduction */}
          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4">Introduction</h2>
            <p className="leading-relaxed">
              Welcome to ArqeraAI ("we," "our," or "us"). We are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website and use our services.
            </p>
          </section>

          {/* Information We Collect */}
          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4">Information We Collect</h2>
            
            <h3 className="text-xl font-semibold text-foreground mb-3 mt-6">Personal Information</h3>
            <p className="leading-relaxed mb-4">
              We may collect personal information that you voluntarily provide to us when you:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Fill out contact forms or inquiry forms</li>
              <li>Schedule consultations or strategy calls</li>
              <li>Complete our AI Transformation Audit</li>
              <li>Subscribe to our newsletter or communications</li>
              <li>Interact with our chat widget or customer support</li>
            </ul>
            <p className="leading-relaxed mt-4">
              This information may include: name, email address, phone number, company name, job title, business information, and any other details you choose to provide.
            </p>

            <h3 className="text-xl font-semibold text-foreground mb-3 mt-6">Automatically Collected Information</h3>
            <p className="leading-relaxed mb-4">
              When you visit our website, we automatically collect certain information about your device and browsing activity, including:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>IP address and device identifiers</li>
              <li>Browser type and version</li>
              <li>Operating system</li>
              <li>Pages visited and time spent on pages</li>
              <li>Referring website addresses</li>
              <li>Geographic location data</li>
            </ul>
          </section>

          {/* How We Use Your Information */}
          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4">How We Use Your Information</h2>
            <p className="leading-relaxed mb-4">
              We use the information we collect for the following purposes:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>To provide, maintain, and improve our services</li>
              <li>To respond to your inquiries and provide customer support</li>
              <li>To schedule and conduct consultations and strategy calls</li>
              <li>To send you marketing communications (with your consent)</li>
              <li>To analyze usage patterns and optimize user experience</li>
              <li>To detect, prevent, and address technical issues or security threats</li>
              <li>To comply with legal obligations and enforce our terms</li>
              <li>To personalize your experience and deliver relevant content</li>
            </ul>
          </section>

          {/* Information Sharing */}
          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4">Information Sharing and Disclosure</h2>
            <p className="leading-relaxed mb-4">
              We do not sell your personal information. We may share your information in the following circumstances:
            </p>
            
            <h3 className="text-xl font-semibold text-foreground mb-3 mt-6">Service Providers</h3>
            <p className="leading-relaxed">
              We may share your information with third-party service providers who assist us in operating our website, conducting our business, or servicing you. These providers include:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4 mt-2">
              <li>Cloud hosting providers</li>
              <li>Email marketing platforms</li>
              <li>Analytics services</li>
              <li>CRM systems</li>
              <li>Payment processors (if applicable)</li>
            </ul>

            <h3 className="text-xl font-semibold text-foreground mb-3 mt-6">Legal Requirements</h3>
            <p className="leading-relaxed">
              We may disclose your information if required by law or in response to valid legal requests, such as subpoenas, court orders, or government regulations.
            </p>

            <h3 className="text-xl font-semibold text-foreground mb-3 mt-6">Business Transfers</h3>
            <p className="leading-relaxed">
              If we are involved in a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction.
            </p>
          </section>

          {/* Data Security */}
          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4">Data Security</h2>
            <p className="leading-relaxed">
              We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. These measures include encryption, secure servers, access controls, and regular security assessments. However, no method of transmission over the internet or electronic storage is 100% secure, and we cannot guarantee absolute security.
            </p>
          </section>

          {/* Cookies and Tracking */}
          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4">Cookies and Tracking Technologies</h2>
            <p className="leading-relaxed mb-4">
              We use cookies and similar tracking technologies to enhance your experience on our website. Cookies are small data files stored on your device that help us:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Remember your preferences and settings</li>
              <li>Understand how you use our website</li>
              <li>Improve website functionality and performance</li>
              <li>Deliver targeted advertising (if applicable)</li>
            </ul>
            <p className="leading-relaxed mt-4">
              You can control cookie settings through your browser preferences. Note that disabling cookies may affect website functionality.
            </p>
          </section>

          {/* Your Rights */}
          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4">Your Privacy Rights</h2>
            <p className="leading-relaxed mb-4">
              Depending on your location, you may have certain rights regarding your personal information:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li><strong>Access:</strong> Request access to the personal information we hold about you</li>
              <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
              <li><strong>Deletion:</strong> Request deletion of your personal information</li>
              <li><strong>Objection:</strong> Object to processing of your personal information</li>
              <li><strong>Portability:</strong> Request transfer of your information to another service</li>
              <li><strong>Withdraw Consent:</strong> Withdraw consent for marketing communications</li>
            </ul>
            <p className="leading-relaxed mt-4">
              To exercise these rights, please contact us at hello@arqeraai.com.
            </p>
          </section>

          {/* Third-Party Links */}
          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4">Third-Party Links</h2>
            <p className="leading-relaxed">
              Our website may contain links to third-party websites or services. We are not responsible for the privacy practices of these third parties. We encourage you to review their privacy policies before providing any personal information.
            </p>
          </section>

          {/* Children's Privacy */}
          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4">Children's Privacy</h2>
            <p className="leading-relaxed">
              Our services are not directed to individuals under the age of 18. We do not knowingly collect personal information from children. If you believe we have inadvertently collected information from a child, please contact us immediately.
            </p>
          </section>

          {/* Data Retention */}
          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4">Data Retention</h2>
            <p className="leading-relaxed">
              We retain your personal information for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law. When information is no longer needed, we will securely delete or anonymize it.
            </p>
          </section>

          {/* International Transfers */}
          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4">International Data Transfers</h2>
            <p className="leading-relaxed">
              Your information may be transferred to and processed in countries other than your country of residence. These countries may have different data protection laws. We ensure appropriate safeguards are in place to protect your information in accordance with this Privacy Policy.
            </p>
          </section>

          {/* Changes to Policy */}
          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4">Changes to This Privacy Policy</h2>
            <p className="leading-relaxed">
              We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements. We will notify you of any material changes by posting the new Privacy Policy on this page and updating the "Last updated" date. Your continued use of our services after changes are posted constitutes acceptance of the updated policy.
            </p>
          </section>

          {/* Contact Information */}
          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4">Contact Us</h2>
            <p className="leading-relaxed mb-4">
              If you have any questions, concerns, or requests regarding this Privacy Policy or our privacy practices, please contact us:
            </p>
            <div className="bg-cyan/5 border border-cyan/20 rounded-lg p-6 mt-4">
              <p className="text-foreground font-semibold mb-2">ArqeraAI</p>
              <p className="mb-1">Email: <a href="mailto:hello@arqeraai.com" className="text-cyan hover:underline">hello@arqeraai.com</a></p>
              <p>Website: <a href="https://arqera.ai" className="text-cyan hover:underline">https://arqera.ai</a></p>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}
